import {dsDtos} from '@vectronic/openApi-customer'
import {Code} from './code'
import {Error} from './error'
import {Parse} from './parse'
import {ParseRange} from './parse.range'
import {Validate} from './validate'

export type ParameterContext = {
  parameters: (dsDtos.Parameter | undefined)[]
  firmwareVersions: dsDtos.FirmwareVersion[]
  pcbTypes: dsDtos.PcbType[]
  subsystems: dsDtos.Subsystem[]
}

export type DeviceContext = {
  device: {
    pcbTypeId?: string
    firmwareVersionId?: string
    parameterDataList?: (dsDtos.ParameterData | undefined)[]
  }
  parameterConfigs: dsDtos.DeviceTypeParameterConfig[]
}

export class ParameterService {
  static code(parameterData: dsDtos.ParameterData, deviceContext: DeviceContext, parameterContext: ParameterContext) {
    const {parameters, subsystems, firmwareVersions, pcbTypes} = parameterContext
    const {device, parameterConfigs} = deviceContext
    const parameter = parameters.find((parameter) => parameter?.id === parameterData.parameterId)
    if (!parameter) throw Error.PARAMETER_NOT_SET
    if (!parameter.write) throw Error.NOT_WRITEABLE
    const parameterConfig = this.getParameterConfig(parameterData, parameterConfigs)
    this.validateCompatibility(parameterConfig, device, firmwareVersions, pcbTypes)
    const subsystem = subsystems.find(({id}) => id === parameter.subsystemId)
    if (!subsystem) throw Error.SUBSYSTEM_NOT_SET
    const isBeaconFrequency = subsystem.identifier === 4 && parameter.identifier === 1
    if (isBeaconFrequency) this.validateBeaconFrequency(parameterData, parameters, subsystem, device)

    const data = Code.do(parameter, parameterData?.data)!
    const isReset = parameterData?.isUserData && parameterData?.data == null
    if (!isReset && !Validate.do(data, parameterConfig.validValueRange ?? parameter.validValueRange))
      throw Error.VALUE_OUT_OF_RANGE
    if (data.byteLength != parameter.length) throw Error.INVALID_BUFFER_LENGTH
    return data
  }

  private static getParameterConfig(
    parameterData: dsDtos.ParameterData,
    parameterConfigs: dsDtos.DeviceTypeParameterConfig[],
  ) {
    const filteredParameterConfigs = parameterConfigs.filter(
      ({parameterId}) => parameterId === parameterData.parameterId,
    )
    if (filteredParameterConfigs?.length !== 1) throw Error.PARAMETER_CONFIG_NOT_SET
    const [parameterConfig] = filteredParameterConfigs
    return parameterConfig
  }

  private static validateCompatibility(
    parameterConfig: dsDtos.DeviceTypeParameterConfig,
    device: DeviceContext['device'],
    firmwareVersions: dsDtos.FirmwareVersion[],
    pcbTypes: dsDtos.PcbType[],
  ) {
    if (parameterConfig.minFirmwareVersion) {
      const firmwareVersion = firmwareVersions.find(({id}) => id === device.firmwareVersionId)
      Validate.firmware(parameterConfig.minFirmwareVersion, firmwareVersion)
    }
    if (parameterConfig.minPcbTypeId) {
      const pcbType = pcbTypes.find(({id}) => id === device.pcbTypeId)
      const minPcbType = pcbTypes.find(({id}) => id === parameterConfig.minPcbTypeId)
      if (!minPcbType) throw Error.PCB_TYPE_NOT_SET
      Validate.pcbType(minPcbType, pcbType)
    }
  }

  private static validateBeaconFrequency(
    parameterData: dsDtos.ParameterData,
    parameters: (dsDtos.Parameter | undefined)[],
    subsystem: dsDtos.Subsystem,
    device: DeviceContext['device'],
  ) {
    const minParameterId = parameters.find(
      (parameter) => parameter?.identifier === 7 && parameter?.subsystemId === subsystem.id,
    )?.id
    if (!minParameterId) throw Error.MIN_BEACON_FREQUENCY_PARAMETER_NOT_SET
    const minData = device.parameterDataList?.find((data) => data?.parameterId === minParameterId)?.data
    if (!minData) throw Error.MIN_BEACON_FREQUENCY_PARAMETER_NOT_SET
    const maxParameterId = parameters.find(
      (parameter) => parameter?.identifier === 8 && parameter?.subsystemId === subsystem.id,
    )?.id
    if (!maxParameterId) throw Error.MAX_BEACON_FREQUENCY_PARAMETER_NOT_SET
    const maxData = device.parameterDataList?.find((data) => data?.parameterId === maxParameterId)?.data
    if (!maxData) throw Error.MAX_BEACON_FREQUENCY_PARAMETER_NOT_SET
    if (isNaN(+minData) || isNaN(+maxData) || isNaN(+parameterData.data!)) throw Error.NAN
    if (+minData > +parameterData.data!) throw `Beacon Frequency has to be bigger than ${minData}`
    if (+maxData < +parameterData.data!) throw `Beacon Frequency has to be less than ${maxData}`
  }

  static isValid(
    parameterData: dsDtos.ParameterData,
    deviceContext: DeviceContext,
    parameterContext: ParameterContext,
  ) {
    this.code(parameterData, deviceContext, parameterContext)
    return true
  }

  static parseRange(parameter: dsDtos.Parameter, config: dsDtos.DeviceTypeParameterConfig) {
    Validate.sameParameterId(parameter, config)
    const validValueRange = config.validValueRange ?? parameter.validValueRange
    if (validValueRange) {
      const {min, max, list, bytewise} = validValueRange
      if (!min && !max && !list && !bytewise) throw Error.INVALID_RANGE
    }
    if (
      ![
        'TIME_ZONE',
        'UNSIGNED_INT',
        'TIME',
        'DAYTIME',
        'DATE_TIME',
        'TIME_SPAN',
        'RANGE',
        'MESSAGE_MODE',
        'ENUMERATION',
        'PHONE',
        'STRING',
      ].includes(parameter.value!)
    ) {
      throw Error.RANGE_VALUETYPE_EXCEPTION
    }
    return ParseRange.do(parameter, validValueRange)
  }

  static parse(parameter: dsDtos.Parameter, data: Buffer) {
    if (data.byteLength != parameter.length) throw Error.INVALID_BUFFER_LENGTH
    return Parse.do(parameter, data)?.toString() ?? null
  }
}
